import merge from 'lodash/merge';
import {isValidElement, useEffect, useRef} from 'react';
import {createPortal} from 'react-dom';
import {useSwipeable} from 'react-swipeable';
import {useDisableWindowScroll} from '../../../common/shared/hooks/useDisableWindowScroll.hook';
import {twMerge} from '../../../common/twMerge';
import {useExecuteInView} from '../../../common/useExecuteInView.hook';
import useStopPropagationOnScroll from '../../../common/useStopPropagationOnScroll';
import {DIALOG_OPEN_DATA_ATTRIBUTE, DIALOG_ZINDEX} from './constants';
import {DialogBottomCombination} from './DialogBottomCombination';
import {DialogButton} from './DialogButton';
import {DialogCaption} from './DialogCaption';
import {DialogCheck} from './DialogCheck';
import {DialogCheckListItem} from './DialogCheckListItem';
import {DialogDimmed} from './DialogDimmed';
import {DialogDivider} from './DialogDivider';
import {DialogImage} from './DialogImage';
import {DialogLabel} from './DialogLabel';
import {DialogList} from './DialogList';
import {DialogOption} from './DialogOption';
import {DialogProps} from './DialogProps.interface';
import {DialogRadio} from './DialogRadio';
import {DialogStar} from './DialogStar';
import {DialogTicketCountControl} from './DialogTicketCountControl';
import {DialogTitle} from './DialogTitle';
import {
  DialogViewerSetting,
  DialogViewerSettingType,
} from './DialogViewerSetting';
import {
  getDialogBottomCombination,
  getDialogContents,
  getDialogDimmed,
  getDialogTitle,
} from './functions';

function DialogMain({
  isOpen,
  isMobile,
  isShadow,
  isFoldableCenterMode = true,
  isUsingScrollBlock = true,
  isList,
  shouldCloseOnDimmedClick = true,
  children,
  onClose,
  closeWhenSwipe,
  onInView,
  className,
  style,
  bottomCombinationClassName,
  ...props
}: DialogProps) {
  const swipeableHandlers = useSwipeable({
    onSwipedUp: () => {
      if (closeWhenSwipe) {
        onClose?.();
      }
    },
    onSwipedDown: () => {
      if (closeWhenSwipe) {
        onClose?.();
      }
    },
  });
  const dialogContentRef = useRef<HTMLDivElement>(null);
  // Dialog 내부 스와이프 버블링을 막음
  useStopPropagationOnScroll(dialogContentRef, true);

  // Dialog Tiara ViewImp 로깅을 위한 부분입니다
  const ref = useExecuteInView({callback: onInView});

  const {disableWindowScroll, enableWindowScroll} = useDisableWindowScroll();

  useEffect(() => {
    // 스크롤 막는 옵션을 끄는 상황에는 disableWindowScroll을 추가하지 않습니다. 뷰어에서 페이지 뷰어 -> 스크롤 뷰어로 이동할때 Dialog를 연상태로 스크롤 해야하기 때문에 스크롤을 막으면 안됩니다.
    if (!isUsingScrollBlock) {
      return;
    }
    if (isOpen) {
      disableWindowScroll();
    } else {
      enableWindowScroll();
    }
  }, [disableWindowScroll, enableWindowScroll, isOpen, isUsingScrollBlock]);

  useEffect(() => {
    const bodyEl = window.document.body;
    if (isOpen && !bodyEl.hasAttribute(DIALOG_OPEN_DATA_ATTRIBUTE)) {
      bodyEl.setAttribute(DIALOG_OPEN_DATA_ATTRIBUTE, '');
    } else if (!isOpen && bodyEl.hasAttribute(DIALOG_OPEN_DATA_ATTRIBUTE)) {
      bodyEl.removeAttribute(DIALOG_OPEN_DATA_ATTRIBUTE);
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      const bodyEl = window.document.body;
      if (bodyEl.hasAttribute(DIALOG_OPEN_DATA_ATTRIBUTE)) {
        bodyEl.removeAttribute(DIALOG_OPEN_DATA_ATTRIBUTE);
      }
    };
  }, []);

  if (!isOpen) {
    return null;
  }
  const dialogTitle = getDialogTitle(children);
  const dialogContents = getDialogContents(children);
  const dialogBottomCombination = getDialogBottomCombination(children);
  const isViewerSetting =
    isValidElement(dialogContents[0]) &&
    dialogContents[0].type === DialogViewerSettingType;

  return createPortal(
    <div
      className={twMerge('fixed inset-0 z-100 flex justify-center', className)}
      style={merge(
        {
          zIndex: DIALOG_ZINDEX,
        },
        style
      )}
      {...props}
      {...swipeableHandlers}
    >
      {/* Dialog Tiara ViewImp 로깅을 위한 부분입니다 */}
      <div ref={ref} />
      <div onClick={shouldCloseOnDimmedClick ? onClose : undefined}>
        {getDialogDimmed(children)}
      </div>
      <div
        className={twMerge(
          'z-100 flex flex-col bg-theme-bg-surface-alt pt-32pxr use-gpu',
          isMobile && 'max-h-[92%] w-full self-end rounded-t-24pxr',
          isFoldableCenterMode &&
            'foldable:max-h-[84%] foldable:w-360pxr foldable:self-center foldable:rounded-32pxr',
          !isMobile && 'max-h-[84%] w-360pxr self-center rounded-32pxr',
          isShadow &&
            'pt-24pxr shadow-[0px_0px_24px_rgba(0,0,0,0.18)] dark:shadow-none',
          isMobile &&
            isViewerSetting &&
            'pb-32pxr bg-theme-bg-surface rounded-t-16pxr'
        )}
        ref={dialogContentRef}
      >
        <div className="overflow-y-auto scrollbar-hide">
          {dialogTitle && (
            <div className="mx-28pxr mb-16pxr">{dialogTitle}</div>
          )}
          {dialogContents && dialogContents.length > 0 && <>{dialogContents}</>}
        </div>
        {dialogBottomCombination && (
          <div className={twMerge('p-11pxr', bottomCombinationClassName)}>
            {dialogBottomCombination}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export const Dialog = Object.assign(DialogMain, {
  Dimmed: DialogDimmed,
  Title: DialogTitle,
  Label: DialogLabel,
  Caption: DialogCaption,
  Option: DialogOption,
  Check: DialogCheck,
  CheckListItem: DialogCheckListItem,
  Button: DialogButton,
  Divider: DialogDivider,
  Image: DialogImage,
  Star: DialogStar,
  TicketCountControl: DialogTicketCountControl,
  List: DialogList,
  Radio: DialogRadio,
  BottomCombination: DialogBottomCombination,
  ViewerSetting: DialogViewerSetting,
});
